import moment from 'moment';
import fuzzysort from 'fuzzysort';
import { action, runInAction } from 'mobx';
import { AxiosError, AxiosResponse } from 'axios';

import { UnreachableFunction, notUndefined } from 'ui/base/types';

import { ServiceStatus, ResultKind } from 'services/base/types';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';

import { Pangolin, PlanFirewallPackage } from 'services/accounts/types';
import { authorizePlanFirewall } from 'services/accounts/logic/plans/plan_firewall';

import { getIssueAffected, getIssueSearchCorpus } from 'services/pangolin/logic/issues/issues';

import { fromBoolean } from 'assets/enums/seen/seen';

import {
  GetIssueResponse,
  GetIssuesResponse,
  IssueIncidentObject,
  IssueIncidentObjectMap,
  IssueIncidentObjectMapTuple,
  IssueObjectMap,
  IssueObjectMapTuple,
  IssueObject,
  SetIssuesRequest,
} from './protos/issues_pb';

import {
  IPangolinDomain,
  Incident,
  IncidentMap,
  Issue,
  IssueMap,
  IssueKind,
  IssueSeverity,
  IssuesFilter,
} from './types';

const LIMIT_PER_PAGE_DEFAULT = 20;
const LIMIT_PER_PAGE_NOPLAN = 5;
const LIMIT_LOADED = 2000;

type PartialIssueWithFPID = Pick<Issue, 'fpid'> & Partial<Issue>;
type PartialIncidentWithUUID = Pick<Incident, 'uuid'> & Partial<Incident>;

export type IssuesFilterApplyResult =
  | { kind: ResultKind.OK; filter: IssuesFilter }
  | {
    kind: ResultKind.SYSTEM_ERROR;
    status?: number;
  }
  | {
    kind: ResultKind.USER_ERROR;
    status?: number;
    seenError?: string;
    affectedError?: string;
    incidentsError?: string;
  };

export type IssuesFetchItemResult =
  | { kind: ResultKind.OK; issue: Issue | undefined }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export type IssuesFetchPageResult =
  | { kind: ResultKind.OK; issues: IssueMap }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export type IssuesUpdateResult =
  | { kind: ResultKind.OK; issue: PartialIssueWithFPID }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export type IssuesMarkSeenResult =
  | { kind: ResultKind.OK; issue: Issue }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export class IssuesService {
  @action
  static clearData(domain: IPangolinDomain) {
    domain.issues = new IssueMap();
    domain.issuesOffset = 0;
    domain.issuesCapped = false;
    domain.issuesStatus = ServiceStatus.FRESH;
  }

  @action
  static clearFilter(domain: IPangolinDomain) {
    const now = new Date();
    domain.issuesFilter = {
      search: '',
      start: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(),
                      now.getHours(), now.getMinutes(), now.getSeconds()),
      end: new Date(now.getFullYear(), now.getMonth(), now.getDate(),
                    now.getHours(), now.getMinutes(), now.getSeconds()),
      seen: { seen: true, unseen: true },
      severityMin: 1.0, severityMax: 5.0,
      affectedMin: NaN, affectedMax: NaN,
      incidentsMin: NaN, incidentsMax: NaN,
      kinds: [], devices: [],
    };
  }

  @action
  static clearFilterDate(domain: IPangolinDomain) {
    const now = new Date();
    domain.issuesFilter = {
      ...domain.issuesFilter,
      start: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(),
                      now.getHours(), now.getMinutes(), now.getSeconds()),
      end: new Date(now.getFullYear(), now.getMonth(), now.getDate(),
                    now.getHours(), now.getMinutes(), now.getSeconds()),
    };
  }

  @action
  static clearAll(domain: IPangolinDomain) {
    IssuesService.clearData(domain);
    IssuesService.clearFilter(domain);
  }

  @action
  static clearDate(domain: IPangolinDomain) {
    IssuesService.clearData(domain);
    IssuesService.clearFilterDate(domain);
  }

  @action
  static async fetchItem(domain: IPangolinDomain, id: string, save = true): Promise<IssuesFetchItemResult> {
    try {
      const httpResp = await http.get(`/pangolin/${domain.id}/issues/${id}`);
      return runInAction(() => {
        const issue = parseIssueResponse(httpResp);
        if (save) domain.issuesItem = issue;
        return Promise.resolve({
          kind: ResultKind.OK, issue,
        });
      });
    } catch(error) {
      return runInAction(() => {
        if (isHttpError(error)) {
          if (error.response?.status && 400 <= error.response.status && error.response.status < 500) {
            return Promise.resolve({ kind: ResultKind.USER_ERROR, status: error.response.status });
          }
          if (error.response?.status && 500 <= error.response.status && error.response.status < 600) {
            return Promise.resolve({ kind: ResultKind.SYSTEM_ERROR, status: error.response.status });
          }
        }
        throw error;
      });
    }
  }

  @action
  static async fetchPage(domain: IPangolinDomain, pangolin: Pangolin, save = true): Promise<IssuesFetchPageResult> {
    try {
      const authorized = authorizePlanFirewall(pangolin, PlanFirewallPackage.BASIC);
      const limit = authorized ? LIMIT_PER_PAGE_DEFAULT : LIMIT_PER_PAGE_NOPLAN;

      const query = domain.issuesFilter;
      const until = query.end.getTime();
      const since = query.start.getTime();
      const offset = domain.issuesOffset;

      if (save) domain.issuesStatus = ServiceStatus.LOADING;
      const httpResp = await http.get(`/pangolin/${domain.id}/issues`, {
        params: {
          base: Math.floor(until / 1000),
          offset, limit,
        },
      });

      if (domain.issuesFilter.end.getTime() !== until) {
        return runInAction(() => {
          if (save) domain.issuesStatus = ServiceStatus.LOADED;
          return Promise.resolve({
            kind: ResultKind.OK,
            issues: domain.issues,
          });
        });
      }

      if (domain.issuesFilter.start.getTime() !== since) {
        return runInAction(() => {
          if (save) domain.issuesStatus = ServiceStatus.LOADED;
          return Promise.resolve({
            kind: ResultKind.OK,
            issues: domain.issues,
          });
        });
      }

      return runInAction(() => {
        const page = parseIssuesResponse(httpResp);
        const filter = createIssuesFilterFunction(domain, query);
        const issues = page.filter(filter);
        if (save) {
          domain.issues.setOrderedMap(issues);
          domain.issuesOffset = offset + page.size;
          domain.issuesCapped = page.size < limit || domain.issues.size > LIMIT_LOADED || ((query) => {
            const tail = page.getAt(page.size - 1)?.incidents.getAt(0)?.timestamp;
            return !tail || !(query.start <= tail && tail <= query.end);
          })(query);
          domain.issuesStatus = ServiceStatus.LOADED;
        }
        return Promise.resolve({
          kind: ResultKind.OK, issues,
        });
      });
    } catch(error) {
      return runInAction(() => {
        if (save) domain.issuesStatus = ServiceStatus.FAILED;
        if (isHttpError(error)) {
          if (error.response?.status && 400 <= error.response.status && error.response.status < 500) {
            return Promise.resolve({ kind: ResultKind.USER_ERROR, status: error.response.status });
          }
          if (error.response?.status && 500 <= error.response.status && error.response.status < 600) {
            return Promise.resolve({ kind: ResultKind.SYSTEM_ERROR, status: error.response.status });
          }
        }
        throw error;
      });
    }
  }

  static async markSeen(domain: IPangolinDomain, issue: Issue, incident: PartialIncidentWithUUID): Promise<IssuesMarkSeenResult> {
    const request = createMarkSeenRequest(issue, incident);
    try {
      await http.put(`/pangolin/${domain.id}/issues`, createRequestString(request));
      runInAction(() => partialUpdateSingleIncident(domain, issue, incident));
      return { kind: ResultKind.OK, issue };
    } catch (error) {
      const status = (error as AxiosError).response?.status;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }

  static async update(domain: IPangolinDomain, issue: PartialIssueWithFPID): Promise<IssuesUpdateResult> {
    const request = createSetIssuesRequest(issue);
    try {
      await http.put(`/pangolin/${domain.id}/issues`, createRequestString(request));
      runInAction(() => partialUpdateSingleIssue(domain, issue));
      return { kind: ResultKind.OK, issue };
    } catch (error) {
      const status = (error as AxiosError).response?.status;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }
}

function pushOneIfNotUndefined<T>(list: T[], value: T | undefined) {
  if (value !== undefined) list.push(value);
}

function pushManyIfNotUndefined<T>(list: T[], values: T[] | undefined) {
  if (values !== undefined) Array.prototype.push.apply(list, values);
}

function createIssuesFilterFunction(domain: IPangolinDomain, filter: IssuesFilter) {
  return (issue: Issue) => {
    const hosts = getIssueAffected(issue);

    // Filter date range
    if (!issue.incidents.some((i) => {
      return (filter.start <= i.timestamp &&
              i.timestamp <= filter.end);
    })) return false;

    // Filter search
    if (filter.search.length > 0 && issue.kind !== IssueKind.UNKNOWN) {
      const locale = localeToLanguageCode(GlobalLocaleStore.get());
      const format: object = { month: 'short', year: 'numeric', day: 'numeric', weekday: 'short',
                               hour: 'numeric', minute: 'numeric', second: 'numeric' };
      const corpus = getIssueSearchCorpus(domain, issue, locale, format);
      const result = fuzzysort.go(filter.search, corpus, { threshold: -100000 });
      if (result.total === 0) return false;
    }

    // Filter action
    const seen = fromBoolean(issue.incidents.every(i => i.seen));
    if (seen == null) return false;
    if (!filter.seen[seen]) return false;

    // Filter severity
    const filterSeverityMin = filter.severityMin;
    const filterSeverityMax = filter.severityMax;
    if (filterSeverityMin == null) return false;
    if (filterSeverityMax == null) return false;
    const issueSeverity = encodeIssueSeverity(issue.severity);
    if (!(filterSeverityMin <= issueSeverity &&
          issueSeverity <= filterSeverityMax))
      return false;

    // Filter affected
    const filterAffectedMin = filter.affectedMin;
    const filterAffectedMax = filter.affectedMax;
    if (filterAffectedMin == null) return false;
    if (filterAffectedMax == null) return false;
    const issueAffected = hosts.length;
    if (!isNaN(filterAffectedMin) &&
        !(filterAffectedMin <= issueAffected))
      return false;
    if (!isNaN(filterAffectedMax) &&
        !(issueAffected <= filterAffectedMax))
      return false;

    // Filter incidents
    const filterIncidentsMin = filter.incidentsMin;
    const filterIncidentsMax = filter.incidentsMax;
    if (filterIncidentsMin == null) return false;
    if (filterIncidentsMax == null) return false;
    const issueIncidents = issue.incidents.size;
    if (!isNaN(filterIncidentsMin) &&
        !(filterIncidentsMin <= issueIncidents))
      return false;
    if (!isNaN(filterIncidentsMax) &&
        !(issueIncidents <= filterIncidentsMax))
      return false;

    // Filter kinds
    if (filter.kinds.length > 0) {
      const kinds: IssueKind[] = [];
      pushOneIfNotUndefined(kinds, issue.kind);
      if (kinds.length === 0) return false;

      const found = filter.kinds.some((a) => kinds.indexOf(a) !== -1);
      if (!found) return false;
    }

    // Filter devices
    if (filter.devices.length > 0) {
      const devices: string[] = [];
      pushManyIfNotUndefined(devices, hosts.map((mac) => domain.hosts.getMac(mac)?.id));
      if (devices.length === 0) return false;

      const found = filter.devices.find((a) => devices.find((b) => a === b));
      if (!found) return false;
    }

    return true;
  };
}

function parseIssueResponse(httpResp: AxiosResponse): Issue | undefined {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetIssueResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getIssue();
  return issueFromProto(itemProto);
}

function parseIssuesResponse(httpResp: AxiosResponse): IssueMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetIssuesResponse.deserializeBinary(rawBytes);
  const listProto = rootProto.getIssues().getItemsList();
  const issueObjects = listProto.map((p) => issueFromProto(p.getData()));
  return new IssueMap(issueObjects.filter(notUndefined).map((i) => [i.fpid, i]));
}

function partialUpdateSingleIssue(domain: IPangolinDomain, issue: PartialIssueWithFPID) {
  const persistent = domain.issues.get(issue.fpid);
  if (persistent != null) Object.assign(persistent, issue);
}

function partialUpdateSingleIncident(domain: IPangolinDomain, issue: Issue, incident: PartialIncidentWithUUID) {
  const persistentIssue = domain.issues.get(issue.fpid);
  const persistentIncident = persistentIssue?.incidents.get(incident.uuid);
  if (persistentIncident != null) Object.assign(persistentIncident, incident);
}

export function issueFromProto(proto: IssueObject | undefined): Issue | undefined {
  if (proto === undefined) return;

  const fpid = proto.getFpid();
  const data = proto.getData();

  if (fpid === undefined) return;
  if (data === undefined) return;

  const kind = parseIssueKind(proto.getKind());
  const severity = parseIssueSeverity(proto.getSeverity());
  const action = proto.getAction() ?? '';
  const incidents = createIncidents(proto.getIncidents());

  const parsed = JSON.parse(data);

  const common = {
    fpid,
    action,
    incidents,
  };

  switch(kind) {
    case IssueKind.MALICIOUS_DOMAIN:
      return {
        ...common,
        kind,
        severity,
        data: {
          mac: parsed.mac,
          domain: parsed.domain,
        },
      };
    case IssueKind.MALICIOUS_ADDRESS:
      return {
        ...common,
        kind,
        severity,
        data: {
          mac: parsed.mac,
          address: parsed.address,
        },
      };
    case IssueKind.MALWARE_DOMAIN:
      return {
        ...common,
        kind,
        severity,
        data: {
          mac: parsed.mac,
          domain: parsed.domain,
          families: parsed.families,
          revision: parsed.revision,
        },
      };
    case IssueKind.MALWARE_ADDRESS:
      return {
        ...common,
        kind,
        severity,
        data: {
          mac: parsed.mac,
          address: parsed.address,
          families: parsed.families,
          revision: parsed.revision,
        },
      };
    case IssueKind.UNKNOWN:
      return {
        ...common,
        kind,
        severity,
      };
    default:
      return UnreachableFunction(kind);
  }
}

export function parseIssueSeverity(severity: number | undefined): IssueSeverity {
  if (severity !== undefined) {
    if (severity >= 5) {
      return IssueSeverity.CRITICAL;
    } else if (severity >= 4) {
      return IssueSeverity.HIGH;
    } else if (severity >= 3) {
      return IssueSeverity.MEDIUM;
    } else if (severity >= 2) {
      return IssueSeverity.LOW;
    } else if (severity >= 1) {
      return IssueSeverity.VERY_LOW;
    } else {
      return IssueSeverity.VERY_LOW;
    }
  }
  return IssueSeverity.VERY_LOW;
}

export function encodeIssueSeverity(severity: IssueSeverity) {
  switch(severity) {
    case IssueSeverity.VERY_LOW:
      return 1.0;
    case IssueSeverity.LOW:
      return 2.0;
    case IssueSeverity.MEDIUM:
      return 3.0;
    case IssueSeverity.HIGH:
      return 4.0;
    case IssueSeverity.CRITICAL:
      return 5.0;
    default:
      return UnreachableFunction(severity);
  }
}

export function parseIssueKind(kind: string | undefined): IssueKind {
  switch(kind) {
    case 'malicious.domain':
      return IssueKind.MALICIOUS_DOMAIN;
    case 'malicious.address':
      return IssueKind.MALICIOUS_ADDRESS;
    case 'malware.domain':
      return IssueKind.MALWARE_DOMAIN;
    case 'malware.address':
      return IssueKind.MALWARE_ADDRESS;
    default:
      return IssueKind.UNKNOWN;
  }
}


function createIncident(proto: IssueIncidentObjectMapTuple): Incident | undefined {
  const id = proto.getId();
  const data = proto.getData();
  if (id == null || data == null) {
    return undefined;
  }
  const timestamp = data.getTimestamp();
  if (timestamp == null) {
    return undefined;
  }
  return {
    uuid: id,
    timestamp: moment.utc(timestamp).toDate(),
    seen: data.getSeen() ?? false,
  };
}

function createIncidents(protos: IssueIncidentObjectMap | undefined): IncidentMap {
  return new IncidentMap(protos?.getItemsList()
                                .map(createIncident)
                                .filter(notUndefined)
                                .map((i) => [i.uuid, i]));
}

export function createSetIssuesRequest(issue: PartialIssueWithFPID): SetIssuesRequest {
  const proto = new IssueObject();
  if (issue.action != null) {
    proto.setAction(issue.action);
  }

  const tuple = new IssueObjectMapTuple();
  tuple.setId(issue.fpid);
  tuple.setData(proto);

  const issues = new IssueObjectMap();
  issues.addItems(tuple);

  const request = new SetIssuesRequest();
  request.setIssues(issues);

  return request;
}

export function createMarkSeenRequest(issue: Issue, incident: PartialIncidentWithUUID): SetIssuesRequest {
  const incidentObject = new IssueIncidentObject();
  if (incident.seen != null) {
    incidentObject.setSeen(incident.seen);
  }

  const incidentTuple = new IssueIncidentObjectMapTuple();
  incidentTuple.setId(incident.uuid);
  incidentTuple.setData(incidentObject);

  const incidents = new IssueIncidentObjectMap();
  incidents.addItems(incidentTuple);

  const issueObject = new IssueObject();
  issueObject.setIncidents(incidents);

  const issueTuple = new IssueObjectMapTuple();
  issueTuple.setId(issue.fpid);
  issueTuple.setData(issueObject);

  const issues = new IssueObjectMap();
  issues.addItems(issueTuple);

  const request = new SetIssuesRequest();
  request.setIssues(issues);

  return request;
}
